import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Scale, AlertTriangle, CheckCircle2, Download, ExternalLink, Filter,
} from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";

export default function CierreAutomatico() {
  const [reports, setReports] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [reportId, setReportId] = useState("");
  const [routeIndex, setRouteIndex] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadIndex = async () => {
    try {
      const { data } = await api.get("/route-reports");
      const flat = [];
      for (const g of data) for (const r of (g.reports || [])) flat.push(r);
      setRouteIndex(flat);
    } catch (e) { /* silent */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (reportId) params.report_id = reportId;
      else {
        if (desde) params.desde = desde;
        if (hasta) params.hasta = hasta;
      }
      const { data } = await api.get("/reports/cierre-automatico", { params });
      setReports(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadIndex(); }, []);
  useEffect(() => { load(); }, [desde, hasta, reportId]);

  const descargarXlsx = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (reportId) params.set("report_id", reportId);
      else {
        if (desde) params.set("desde", desde);
        if (hasta) params.set("hasta", hasta);
      }
      const res = await fetch(`${API_BASE}/reports/cierre-automatico.xlsx?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("No se pudo exportar");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `cierre_automatico_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success("Exportado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totales = reports.reduce((acc, r) => {
    acc.pedidos += r.total_pedidos;
    acc.esperado += r.total_esperado;
    acc.cobrado += r.total_cobrado;
    acc.diferencia += r.total_diferencia;
    acc.errores += r.errores_count;
    return acc;
  }, { pedidos: 0, esperado: 0, cobrado: 0, diferencia: 0, errores: 0 });

  return (
    <div className="space-y-6" data-testid="cierre-automatico-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Cierre</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Scale className="w-8 h-8 text-[#F59E0B]" /> Cierre Automático
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Compara automáticamente lo esperado según el Reporte de Ruta contra lo realmente cobrado en Facturación. Detecta diferencias y errores por pedido.
          </p>
        </div>
        <Button
          onClick={descargarXlsx}
          className="bg-[#002FA7] hover:bg-[#00227A] gap-2"
          disabled={reports.length === 0}
          data-testid="cierre-export-btn"
        >
          <Download className="w-4 h-4" /> Exportar XLSX
        </Button>
      </header>

      {/* Filtros */}
      <Card className="p-4 border-zinc-200 rounded-md">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 font-semibold">
            <Filter className="w-4 h-4" /> Filtro
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Reporte específico</Label>
            <Select value={reportId || "all"} onValueChange={(v) => setReportId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="cierre-report-select"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos (usar rango)</SelectItem>
                {routeIndex.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.codigo_reporte} — {(r.fecha || "").slice(0,10)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={reportId ? "opacity-40 pointer-events-none" : ""}>
            <DateRangeFilter desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} testidPrefix="cierre" showClear={false} />
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos</div>
          <div className="font-manrope font-black text-3xl mt-1">{totales.pedidos}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Esperado</div>
          <div className="font-manrope font-black text-3xl mt-1 text-blue-700">{formatMoney(totales.esperado)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cobrado</div>
          <div className="font-manrope font-black text-3xl mt-1 text-emerald-700">{formatMoney(totales.cobrado)}</div>
        </Card>
        <Card className={`p-4 border-zinc-200 rounded-md ${totales.errores > 0 ? "border-red-300 bg-red-50" : ""}`}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos con error</div>
          <div className={`font-manrope font-black text-3xl mt-1 ${totales.errores > 0 ? "text-red-700" : "text-emerald-700"}`}>{totales.errores}</div>
        </Card>
      </div>

      {loading && <div className="text-sm text-zinc-500">Comparando...</div>}
      {!loading && reports.length === 0 && (
        <Card className="p-8 text-center text-zinc-400 border-zinc-200 rounded-md">
          <Scale className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
          No hay reportes de ruta para el filtro seleccionado.
        </Card>
      )}

      <ConciliacionManualPanel />

      {reports.map((r) => (
        <Card key={r.report_id} className="border-zinc-200 rounded-md overflow-hidden" data-testid={`cierre-report-${r.codigo_reporte}`}>
          <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Reporte de Ruta</div>
              <div className="font-manrope font-black text-lg">{r.codigo_reporte}</div>
              <div className="text-xs text-zinc-500">{(r.fecha || "").slice(0, 19).replace("T", " ")} · {r.generado_por}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Esperado / Cobrado</div>
                <div className="font-mono text-sm">{formatMoney(r.total_esperado)} / <span className="text-emerald-700">{formatMoney(r.total_cobrado)}</span></div>
              </div>
              <div className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest ${r.errores_count > 0 ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>
                {r.errores_count > 0 ? <><AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />{r.errores_count} con error</> : <><CheckCircle2 className="w-3 h-3 inline -mt-0.5 mr-1" />OK</>}
              </div>
            </div>
          </div>

          {/* Dual view: expected (left) vs actual (right) */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th colSpan={5} className="px-3 py-2 text-xs uppercase tracking-widest font-semibold bg-blue-50 border-r border-zinc-200">← Reporte de Ruta (Esperado)</th>
                  <th colSpan={5} className="px-3 py-2 text-xs uppercase tracking-widest font-semibold bg-emerald-50">Facturación (Real) →</th>
                </tr>
                <tr className="text-left border-t border-zinc-200">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Pedido</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Vendedor</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Cliente</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Zona</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right border-r border-zinc-200">A cobrar</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Factura(s)</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Cobrado</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Método</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Entrega</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Errores</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {r.filas.map((f, i) => (
                  <tr key={i} className={f.tiene_error ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"} data-testid={`cierre-row-${f.pedido}`}>
                    <td className="px-3 py-2">
                      <Link to={`/pedidos/${f.pedido_id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline inline-flex items-center gap-1">
                        {f.pedido} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs">{f.vendedor || "—"}</td>
                    <td className="px-3 py-2 text-xs">{f.cliente}</td>
                    <td className="px-3 py-2 text-xs text-zinc-600 max-w-[180px] truncate" title={f.zona}>{f.zona || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold border-r border-zinc-200">{formatMoney(f.a_cobrar)}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{f.factura || <span className="text-zinc-300">—</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{formatMoney(f.cobrado)}</td>
                    <td className="px-3 py-2 text-xs">{f.metodo_pago || <span className="text-zinc-300">—</span>}</td>
                    <td className="px-3 py-2 text-xs capitalize">{f.estado_entrega || "—"}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {f.errores.length > 0 ? (
                        <div className="space-y-0.5">
                          {f.errores.map((err, k) => (
                            <div key={k} className="text-red-700 flex items-start gap-1">
                              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>{err}</span>
                            </div>
                          ))}
                        </div>
                      ) : <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-zinc-100">
                <tr className="font-semibold">
                  <td colSpan={4} className="px-3 py-2 text-xs uppercase tracking-widest">Totales</td>
                  <td className="px-3 py-2 text-right tabular-nums border-r border-zinc-200">{formatMoney(r.total_esperado)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatMoney(r.total_cobrado)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Conciliación por método de pago */}
          {r.por_metodo.length > 0 && (
            <div className="p-4 border-t border-zinc-200 bg-white">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
                Conciliación por Método de Pago (según facturas)
              </div>
              <div className="flex flex-wrap gap-3">
                {r.por_metodo.map((m) => (
                  <div key={m.metodo} className="px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200 min-w-[140px]" data-testid={`cierre-metodo-${m.metodo}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{m.metodo}</div>
                    <div className="font-manrope font-black text-lg mt-0.5">{formatMoney(m.total)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                Compara estos montos con lo físico usando <Link to="/conciliacion" className="text-[#002FA7] hover:underline font-semibold">Conciliación Bancaria</Link> del día {r.fecha_dia}.
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function ConciliacionManualPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), pedido: "", autorizacion: "", monto: 0, banco: "", metodo_pago: "", concepto: "", otro_dato: "" });
  const load = async () => {
    try { const { data } = await api.get("/conciliacion-manual"); setRows(data); } catch {}
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.pedido.trim()) return toast.error("Número de pedido requerido");
    if (!form.autorizacion.trim()) return toast.error("Autorización requerida");
    if (!form.monto || form.monto <= 0) return toast.error("Monto > 0 requerido");
    if (!form.banco.trim()) return toast.error("Banco requerido");
    try {
      await api.post("/conciliacion-manual", form);
      toast.success("Registrado");
      setForm({ ...form, pedido: "", autorizacion: "", monto: 0, concepto: "", otro_dato: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/conciliacion-manual/${id}`); load(); } catch {}
  };
  return (
    <Card className="p-5 border-zinc-200 rounded-md" data-testid="revision-fisica-panel">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-2">
        <Scale className="w-3 h-3" /> Revisión Física — Registro manual del Estado de Cuenta
      </div>
      <p className="text-xs text-zinc-500 mb-3">Se llena manualmente según los estados de cuenta. Escribe el <b>Pedido</b>, la <b>fecha del depósito</b>, la <b>autorización</b>, el <b>monto</b> y el <b>banco</b>.</p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <div><Label className="text-xs">Pedido *</Label><Input value={form.pedido} onChange={(e) => setForm({ ...form, pedido: e.target.value })} placeholder="PED-A0001" data-testid="rf-pedido" /></div>
        <div><Label className="text-xs">Fecha depósito *</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="rf-fecha" /></div>
        <div><Label className="text-xs">Autorización *</Label><Input value={form.autorizacion} onChange={(e) => setForm({ ...form, autorizacion: e.target.value })} data-testid="rf-auth" /></div>
        <div><Label className="text-xs">Monto *</Label><Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: Number(e.target.value) })} data-testid="rf-monto" /></div>
        <div><Label className="text-xs">Banco *</Label><Input value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} data-testid="rf-banco" /></div>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div><Label className="text-xs">Método</Label><Input value={form.metodo_pago} onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} placeholder="Transferencia/Depósito..." data-testid="rf-metodo" /></div>
        <div><Label className="text-xs">Concepto / Otro dato</Label><Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder="Detalle" data-testid="rf-concepto" /></div>
        <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="rf-save">Agregar</Button>
      </div>

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Fecha depósito</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Autorización</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">Monto</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Banco</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Método</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Concepto</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50" data-testid={`rf-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#002FA7]">{r.pedido || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.fecha}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.autorizacion}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(r.monto)}</td>
                  <td className="px-3 py-2 text-xs">{r.banco || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.metodo_pago || "—"}</td>
                  <td className="px-3 py-2 text-xs text-zinc-600 max-w-xs truncate" title={r.concepto}>{r.concepto || "—"}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => del(r.id)} className="text-xs text-red-600 hover:underline">Eliminar</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
